/**
 * useApiMutation — hook resilient pour POST / PUT / DELETE / PATCH
 * ---------------------------------------------------------------------------
 * Frere de useApiQuery. Centralise le pattern :
 *   try { axios.post(...); toast.success } catch { toast.error }
 *
 * Avantages :
 *  • Lock `isMutating` — empeche le double-clic accidentel.
 *  • Parse JSON safe (HTML 502 du proxy n'explose pas).
 *  • Erreur structuree : { type, status, detail } — comme useApiQuery.
 *  • Auto-toast d'erreur (peut etre desactive via { silentError: true }).
 *  • Support body JSON ou FormData (multipart upload sans header magique).
 *  • Bearer token auto via tokenService.getToken() (ou desactive via authenticated:false).
 *  • Retourne le data du serveur si succes, sinon throw (pour await + custom catch).
 *
 * Usage typique :
 *   const { mutate, isMutating } = useApiMutation('POST', '/api/admin/membres/upload');
 *
 *   const onSubmit = async () => {
 *     try {
 *       const data = await mutate({ nom, prenom });
 *       toast.success('Membre cree');
 *       refetchMembres();
 *     } catch (e) {
 *       // Toast d'erreur deja affiche par le hook (sauf silentError)
 *     }
 *   };
 *
 * Pour upload de fichier :
 *   const fd = new FormData();
 *   fd.append('file', file);
 *   await mutate(fd);
 *
 * Pour DELETE sans body :
 *   await mutate();
 */
import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { getToken } from '../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _DEFAULTS = {
  authenticated: true,
  silentError: false,
  successToast: null,           // string ou null — pas de toast succes par defaut
  errorPrefix: 'Erreur',         // prefixe dans le toast
};

export function useApiMutation(method, path, options = {}) {
  const opts = { ..._DEFAULTS, ...options };
  const [isMutating, setIsMutating] = useState(false);
  const abortRef = useRef(null);
  const M = String(method || 'POST').toUpperCase();

  const mutate = useCallback(async (body) => {
    if (abortRef.current) { try { abortRef.current.abort(); } catch {} }
    const controller = new AbortController();
    abortRef.current = controller;
    setIsMutating(true);

    try {
      const headers = { Accept: 'application/json' };
      if (opts.authenticated) {
        const token = getToken();
        if (!token) {
          const err = { type: 'auth', status: 401, detail: 'Session expiree' };
          if (!opts.silentError) toast.error(`${opts.errorPrefix} : ${err.detail}`);
          throw err;
        }
        headers.Authorization = `Bearer ${token}`;
      }

      // Encode body : FormData laisse passer (le browser pose Content-Type),
      // sinon on envoie en JSON.
      let serializedBody;
      if (body === undefined || body === null) {
        serializedBody = undefined;
      } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
        serializedBody = body;
      } else {
        headers['Content-Type'] = 'application/json';
        serializedBody = JSON.stringify(body);
      }

      const url = path.startsWith('http') ? path : `${API_URL}${path}`;
      const res = await fetch(url, { method: M, headers, body: serializedBody, signal: controller.signal });

      // V36.44 — Lire le body en TEXTE UNE SEULE FOIS (pattern robuste).
      // L'ancien `res.clone().json()` echoue ("Response body is already used")
      // dans certains cas (React StrictMode re-render, retries internes).
      let bodyText = '';
      try { bodyText = await res.text(); } catch { /* network mid-fetch */ }
      let data = null;
      if (bodyText) {
        try { data = JSON.parse(bodyText); } catch { /* not JSON */ }
      }

      if (res.ok) {
        if (opts.successToast) toast.success(opts.successToast);
        return data;
      }

      const isGateway = [502, 503, 504].includes(res.status);
      const err = {
        type: res.status === 401 ? 'auth' : (isGateway ? 'gateway' : 'http'),
        status: res.status,
        detail: data?.detail || `HTTP ${res.status}`,
      };
      if (!opts.silentError) toast.error(`${opts.errorPrefix} : ${err.detail}`);
      throw err;
    } catch (err) {
      // Si c'est notre erreur structuree, on la rejette telle quelle (toast deja affiche)
      if (err && err.type) throw err;
      // AbortController : silencieux
      if (err?.name === 'AbortError') {
        const aborted = { type: 'aborted', status: 0, detail: 'Requete annulee' };
        throw aborted;
      }
      // Erreur reseau brute
      const netErr = { type: 'network', status: 0, detail: 'Erreur de connexion au serveur' };
      if (!opts.silentError) toast.error(`${opts.errorPrefix} : ${netErr.detail}`);
      throw netErr;
    } finally {
      setIsMutating(false);
    }
  }, [M, path, opts.authenticated, opts.silentError, opts.successToast, opts.errorPrefix]);

  const cancel = useCallback(() => {
    if (abortRef.current) { try { abortRef.current.abort(); } catch {} }
  }, []);

  return { mutate, isMutating, cancel };
}

export default useApiMutation;
